import { useMemo } from "react";

interface UserAgentInfo {
	browser: {
		name: string;
		version: string;
	};
	os: {
		name: string;
		version: string;
	};
	device: {
		type: string;
		vendor: string;
		model: string;
	};
	engine: {
		name: string;
		version: string;
	};
	raw: string;
}

export const useUserAgent = (): UserAgentInfo => {
	return useMemo(() => {
		const userAgent = navigator.userAgent;

		// Browser detection
		const getBrowserInfo = () => {
			const browsers = [
				{ name: "Chrome", regex: /Chrome\/(\d+(?:\.\d+)*)/ },
				{ name: "Firefox", regex: /Firefox\/(\d+(?:\.\d+)*)/ },
				{ name: "Safari", regex: /Version\/(\d+(?:\.\d+)*)\D*Safari/ },
				{ name: "Edge", regex: /Edg\/(\d+(?:\.\d+)*)/ },
				{ name: "Opera", regex: /OPR\/(\d+(?:\.\d+)*)/ },
				{ name: "Internet Explorer", regex: /MSIE (\d+(?:\.\d+)*)/ },
			];

			for (const browser of browsers) {
				const match = browser.regex.exec(userAgent);

				if (match) {
					return { name: browser.name, version: match[1] };
				}
			}

			return { name: "Unknown", version: "Unknown" };
		};

		// OS detection
		const getOSInfo = () => {
			const systems = [
				{ name: "Windows 11", regex: /Windows NT 10\.0[^;]*rv:/ },
				{ name: "Windows 10", regex: /Windows NT 10\.0/ },
				{ name: "Windows 8.1", regex: /Windows NT 6\.3/ },
				{ name: "Windows 8", regex: /Windows NT 6\.2/ },
				{ name: "Windows 7", regex: /Windows NT 6\.1/ },
				{ name: "macOS", regex: /Mac OS X ([\d_]+)/ },
				{ name: "iOS", regex: /OS ([\d_]+) like Mac OS X/ },
				{ name: "Android", regex: /Android (\d+(?:\.\d+)*)/ },
				{ name: "Linux", regex: /Linux/ },
			];

			for (const system of systems) {
				const match = system.regex.exec(userAgent);

				if (match) {
					const version = match[1] ? match[1].replaceAll("_", ".") : "Unknown";

					return { name: system.name, version };
				}
			}

			return { name: "Unknown", version: "Unknown" };
		};

		// Device detection
		const getDeviceInfo = () => {
			// Mobile detection
			if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(userAgent)) {
				if (/iPhone/i.test(userAgent)) {
					const match = /iPhone OS ([\d_]+)/.exec(userAgent);
					const model = match?.[1]?.replaceAll("_", ".") || "";

					return { type: "Mobile", vendor: "Apple", model: `iPhone (iOS ${model})` };
				}
				if (/iPad/i.test(userAgent)) {
					const match = /OS ([\d_]+)/.exec(userAgent);
					const model = match?.[1]?.replaceAll("_", ".") || "";

					return { type: "Tablet", vendor: "Apple", model: `iPad (iOS ${model})` };
				}
				if (/Android/i.test(userAgent)) {
					const match = /Android (\d+(?:\.\d+)*);[^;)]*([^;)]+)\)?/.exec(userAgent);
					const model = match?.[2]?.trim() || "Android Device";

					return { type: "Mobile", vendor: "Android", model };
				}

				return { type: "Mobile", vendor: "Unknown", model: "Unknown" };
			}

			// Tablet detection
			if (/Tablet|iPad/i.test(userAgent)) {
				return { type: "Tablet", vendor: "Unknown", model: "Unknown" };
			}

			// Desktop
			return { type: "Desktop", vendor: "Unknown", model: "Unknown" };
		};

		// Engine detection
		const getEngineInfo = () => {
			const engines = [
				{ name: "Blink", regex: /Chrome\/(\d+(?:\.\d+)*)/ },
				{ name: "WebKit", regex: /WebKit\/(\d+(?:\.\d+)*)/ },
				{ name: "Gecko", regex: /Gecko\/(\d+(?:\.\d+)*)/ },
				{ name: "Trident", regex: /Trident\/(\d+(?:\.\d+)*)/ },
			];

			for (const engine of engines) {
				const match = engine.regex.exec(userAgent);

				if (match) {
					return { name: engine.name, version: match[1] };
				}
			}

			return { name: "Unknown", version: "Unknown" };
		};

		return {
			browser: getBrowserInfo(),
			os: getOSInfo(),
			device: getDeviceInfo(),
			engine: getEngineInfo(),
			raw: userAgent,
		};
	}, []);
};
